import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import moment from 'moment-timezone';
import React from 'react';
import EnhancedTableHead, { getSorting, HeadRow, Order, sortRows } from '../../common/EnhancedTableHead';
import LinkRouter from '../../common/LinkRouter';
import TimestampTooltip from '../../common/TimestampTooltip';

interface Props {
  workflows: any[];
  familyId: string;
  page: number;
  showArchivedColumn: boolean;
  onPageChange: (newPage: number) => void;
  onExecuteWorkflow: (workflow: any) => void;
  onToggleSubscription: (workflow: any) => void;
  onToggleArchiveState: (workflow: any) => void;
}

const headRows: HeadRow[] = [
  { id: 'name', disablePadding: true, label: 'Name' },
  { id: 'account', disablePadding: false, label: 'Account' },
  { id: 'region', disablePadding: false, label: 'Region' },
  { id: 'lastModified', disablePadding: false, label: 'Last Modified' },
  { id: 'scheduled', disablePadding: false, label: 'Scheduled Time' },
  { id: 'actions', disablePadding: false, label: '' }
];

function WorkflowsTable(props: Props) {
  const { workflows, familyId, page, showArchivedColumn, onPageChange } = props;
  const [order, setOrder] = React.useState<Order>('desc');
  const [orderBy, setOrderBy] = React.useState<string>('lastModified');
  const [rowsPerPage, setRowsPerPage] = React.useState(20);

  function handleRequestSort(event: React.MouseEvent<unknown>, property: string) {
    const isDesc = orderBy === property && order === 'desc';
    setOrder(isDesc ? 'asc' : 'desc');
    setOrderBy(property);
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    onPageChange(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    onPageChange(newPage);
  };

  const handleArchiveCheckboxChange = (workflow: any) => (event: React.ChangeEvent<HTMLInputElement>) => {
    props.onToggleArchiveState(workflow);
  };

  const handleExecuteWorkflow = (workflow: any) => () => {
    props.onExecuteWorkflow(workflow);
  };

  const sortedRows = sortRows(workflows, getSorting(order, orderBy));

  return (
    <div>
      <Table>
        <EnhancedTableHead
          order={order}
          orderBy={orderBy}
          fields={!showArchivedColumn ? headRows : [...headRows, { id: 'archived', disablePadding: false, label: 'Archived' }]}
          onRequestSort={handleRequestSort}
        />
        <TableBody>
          {sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(row => (
            <TableRow key={row.id}>
              <TableCell component="th" scope="row">
                <LinkRouter variant="body1" to={`/families/${familyId}/workflows/${row.id}`}>{row.name}</LinkRouter>
              </TableCell>
              <TableCell>{row.account}</TableCell>
              <TableCell>{row.region}</TableCell>
              <TableCell>
                <TimestampTooltip timestamp={row.lastModified}>
                  <div>{moment(row.lastModified).fromNow()}</div>
                </TimestampTooltip>
              </TableCell>
              <TableCell>{row.scheduled}</TableCell>
              <TableCell>
                <Tooltip title="Execute this workflow">
                  <IconButton onClick={handleExecuteWorkflow(row)}><PlayArrowIcon /></IconButton>
                </Tooltip>
              </TableCell>
              {showArchivedColumn && <TableCell>
                <Checkbox
                  checked={!!row.archived}
                  onChange={handleArchiveCheckboxChange(row)}
                  value="true"
                  color="primary"
                />
              </TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10, 20, 50]}
        component="div"
        count={workflows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        backIconButtonProps={{
          'aria-label': 'previous page',
        }}
        nextIconButtonProps={{
          'aria-label': 'next page',
        }}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </div>
  );
};

export default WorkflowsTable;
